import { Box, Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import { useEnvironmentType } from "apps/web/src/contexts/useEnvType";
import { useBrowserBackAndRefreshWarning } from "apps/web/src/hooks/useBrowserBackAndRefreshWarning";
import { useNutritionFlags } from "apps/web/src/hooks/useNutritionFlags";
import { type RecipeAiFlow } from "apps/web/src/hooks/useRevisionGatedAiFlow";
import { GptRecipeFragment, RecipeFullFragment } from "apps/web/src/types";
import React, { useCallback, useReducer, useState } from "react";
import { newServingAmount } from "shared-ui/ServingAmount/utils";
import { newRecipeFormState, newRecipeFormStateFromGPTRecipe, recipeFormReducer } from "../../Recipe/Form/utils";
import { GenerateWithGPTDialog } from "../../Recipe/GPT/Generator/GenerateWithGPTDialog";
import { openTrackedRecipeAiFlow } from "../../Recipe/GPT/recipeAiFlow";
import { TranscribeWithGPTDialog } from "../../Recipe/GPT/Transcriber/TranscribeWithGPTDialog";
import { TranscribeError, getUserFriendlyMessage } from "../../Recipe/GPT/Transcriber/utils";
import RecipeCreateDialog from "../../Recipe/RecipeCreateDialog";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import DiscardChangesDialog from "../../universal/DiscardChangesDialog";
import { MenuItemFullEditForm } from "../Form/Form";
import { CreateMenuItemHeader } from "../Header/CreateMenuItemHeader";
import { MenuItemState, menuItemReducer } from "../reducer";
import { newMenuItemState } from "../utils";
import { getMenuItemDialogTitle } from "./getMenuItemDialogTitle";

interface MenuItemDialogContentProps {
  open: boolean;
  onClose: () => void;
  onSave: (state: MenuItemState) => void;
  title: string;
  variant: "MenuItem" | "MenuItemAppearance";
  initState?: MenuItemState;
  allowSaveAsRecipe?: boolean;
  /** When true, render as a page (no Dialog shell). Used by Menu Item create routes. */
  pageView?: boolean;
}

const MenuItemDialogContent = ({
  open,
  onClose,
  onSave,
  title,
  initState,
  variant,
  allowSaveAsRecipe,
  pageView = false,
}: MenuItemDialogContentProps) => {
  const [state, dispatch] = useReducer(menuItemReducer, initState || newMenuItemState("", false));
  const [initialRecipeState, initialRecipeDispatch] = useReducer(
    recipeFormReducer,
    undefined || newRecipeFormState(initState?.menuItem.name, initState?.menuItem.servingAmounts)
  );
  const { setMessage } = useSnackbar();
  const [discardChangesOpen, setDiscardChangesOpen] = useState(false);
  const [showGenerateRecipeModal, setShowGenerateRecipeModal] = useState(false);
  const [showTranscribePhotoModal, setShowTranscribePhotoModal] = useState(false);
  const [showImportLinkModal, setShowImportLinkModal] = useState(false);
  const [transcriberError, setTranscriberError] = useState<TranscribeError | null>(null);
  const [showCreateRecipeModal, setShowCreateRecipeModal] = useState(false);

  const { isCommercial } = useEnvironmentType();
  const { isRecipeAndMenuItemRedesignEnabled, isGetRecipeFromUrlEnabled } = useNutritionFlags();
  const showAiRecipes = isCommercial;
  // Redesign is Kitchen → Menu Items create/edit pages only (not nested dialogs).
  const useMenuItemRedesign = pageView && isRecipeAndMenuItemRedesignEnabled;
  const dialogTitle = getMenuItemDialogTitle(title, useMenuItemRedesign);

  const openAiFlow = useCallback((flow: RecipeAiFlow) => {
    openTrackedRecipeAiFlow(flow, "Menu item", {
      generate: () => setShowGenerateRecipeModal(true),
      photo: () => setShowTranscribePhotoModal(true),
      link: () => setShowImportLinkModal(true),
    });
  }, []);

  const onAiGenerateClicked = () => openAiFlow("generate");
  const onAiTranscribeClicked = () => openAiFlow("photo");
  const onImportFromLinkClicked = () => openAiFlow("link");
  const { setBrowserBackAndRefreshWarningEnabled } = useBrowserBackAndRefreshWarning();

  React.useEffect(() => {
    setBrowserBackAndRefreshWarningEnabled(state.edited);
  }, [state.edited, setBrowserBackAndRefreshWarningEnabled]);

  const handleClose = () => {
    if (state.edited) {
      setDiscardChangesOpen(true);
    } else {
      onClose();
    }
  };

  const onGenerateError = (error: any) => {
    const userFriendlyMessage = getUserFriendlyMessage(error);
    setMessage("error", userFriendlyMessage);
  };

  const onSaveAiRecipe = (recipe: RecipeFullFragment) => {
    setMessage("success", `${recipe.name} has been created.`);
    const newRecipeServing = recipe.servings[0];
    const newRecipeServingAmounts = [
      newServingAmount(
        {
          ...newRecipeServing,
          foodOrRecipe: recipe,
        },
        1
      ),
    ];
    dispatch({
      type: "MenuItemChangeServingAmountsAction",
      payload: {
        servingAmounts: newRecipeServingAmounts,
      },
    });
  };

  const onGPTRecipeComplete = (gptRecipe: GptRecipeFragment, closeModal: () => void) => {
    const newState = newRecipeFormStateFromGPTRecipe(gptRecipe);
    initialRecipeDispatch({
      type: "REPLACE_STATE",
      payload: newState,
    });
    closeModal();
    setShowCreateRecipeModal(true);
  };

  const form = (
    <MenuItemFullEditForm
      state={state}
      dispatch={dispatch}
      variant={variant}
      onAiGenerateClicked={showAiRecipes ? onAiGenerateClicked : null}
      onAiTranscribeClicked={showAiRecipes ? onAiTranscribeClicked : undefined}
      onImportFromLinkClicked={showAiRecipes && isGetRecipeFromUrlEnabled ? onImportFromLinkClicked : undefined}
      allowSaveAsRecipe={allowSaveAsRecipe}
      enableRedesign={useMenuItemRedesign}
    />
  );

  const nestedDialogs = (
    <>
      {discardChangesOpen && (
        <DiscardChangesDialog open={discardChangesOpen} onClose={() => setDiscardChangesOpen(false)} onDiscard={onClose} />
      )}
      {showGenerateRecipeModal && (
        <GenerateWithGPTDialog
          open={showGenerateRecipeModal}
          onClose={() => setShowGenerateRecipeModal(false)}
          onGenerateComplete={onGPTRecipeComplete}
          onError={onGenerateError}
          initialName={state.menuItem.name}
        />
      )}
      {showTranscribePhotoModal && (
        <TranscribeWithGPTDialog
          open={showTranscribePhotoModal}
          onClose={() => setShowTranscribePhotoModal(false)}
          onTranscribeComplete={onGPTRecipeComplete}
          transcriberError={transcriberError}
          setTranscriberError={setTranscriberError}
          onError={onGenerateError}
          mode="photo"
        />
      )}
      {showImportLinkModal && (
        <TranscribeWithGPTDialog
          open={showImportLinkModal}
          onClose={() => setShowImportLinkModal(false)}
          onTranscribeComplete={onGPTRecipeComplete}
          transcriberError={transcriberError}
          setTranscriberError={setTranscriberError}
          onError={onGenerateError}
          mode="link"
        />
      )}
      {showCreateRecipeModal && (
        <RecipeCreateDialog
          open={showCreateRecipeModal}
          onClose={() => setShowCreateRecipeModal(false)}
          onCreate={recipe => onSaveAiRecipe(recipe)}
          initialRecipeState={initialRecipeState}
        />
      )}
    </>
  );

  if (useMenuItemRedesign) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <CreateMenuItemHeader
          title={dialogTitle}
          handleClose={handleClose}
          handleSave={() => onSave(state)}
          saveLabel={variant === "MenuItemAppearance" ? "Done" : "Save"}
          pageView
        />
        {form}
        {nestedDialogs}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      PaperProps={{
        sx: { minHeight: "calc(100% - 20px)" },
      }}
      fullWidth
    >
      <DialogTitle title={dialogTitle} onClose={handleClose} />
      <DialogContent>{form}</DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Close
        </Button>
        <Button onClick={() => onSave(state)}>{variant === "MenuItemAppearance" ? "Done" : "Save"}</Button>
      </DialogActions>
      {nestedDialogs}
    </Dialog>
  );
};

export default MenuItemDialogContent;
